import React, { useState, useEffect, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';

import { FormHelperText, FormCheckboxList, FormTextInput } from "components/form-fields";
import { useDebounce } from 'hooks/useDebounce';
import { ListingPagination } from 'components/listing';
import { ShowHide } from 'components/layout';
import { TagList } from 'components/status';
import { NonIdealState } from '@blueprintjs/core';
import classNames from 'classnames';
import 'components/form-fields/FormFields.css';

export function FormPagedCheckboxList(
    {
        headingText = '',
        placeholder = 'Search items',
        id = 'paged-checkbox',
        disabled = false,
        loading = false,
        loadingPage = false,
        saving = false,
        noDataMessage = 'No results found, please try a different search term.',
        dangerHelperText = '',
        fill = false,
        items,
        onItemSelect,
        onPageChange,
        selectedItems,
        totalItemCount
    }
) {
    const debounceTimeout = 750;

    const [searchTerm, setSearchTerm] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsValue, setItems] = useState([]);

    const listSize = 50;
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const inputSearchRef = useRef(null);

    const dummyItems = [];
    const numberOfDummyItems = 10;

    for (let i = 0; i < numberOfDummyItems; i++) {
        dummyItems.push({ id: i, name: '', checked: false });
    }

    useEffect(() => {
        onPageChangeValue(1);
    }, [debouncedSearchTerm]);

    useEffect(() => {
        const itemsClone = [...items];

        for (let item of itemsClone) {
            item.checked = selectedItems?.some(x => x.id === item.id);
        }

        setItems(itemsClone);
    }, [items, selectedItems, saving]);

    useEffect(() => {
        setTotalPages(Math.ceil(totalItemCount / listSize));
    }, [totalItemCount]);

    function onItemChange(event, idValue) {
        const isChecked = event?.target?.checked != null && event.target.checked;
        const item = itemsValue.find(x => x.id === idValue);
        onItemSelect(item, isChecked);
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function onPageChangeValue(newPage) {
        setPageNumber(newPage);
        onPageChange(listSize, newPage, debouncedSearchTerm);
    }

    function onRemoveTag(event, idValue) {
        const item = selectedItems.find(x => x.id === idValue);
        onItemSelect(item, false);
    }

    function sortItems(a, b) {
        var nameA = a.name.toUpperCase();
        var nameB = b.name.toUpperCase();
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }

        // names must be equal
        return 0;

    }

    return (
        <div className='form-field'>
            <ShowHide
                evaluator={headingText != null}
                show={(
                    <h4 className={classNames({ "bp6-skeleton": loading })}>{headingText}</h4>
                )}
            />
            <TagList items={selectedItems.sort(sortItems)} disabled={saving || disabled} onRemoveTag={onRemoveTag} />
            <ShowHide
                evaluator={disabled}
                hide={(
                    <Fragment>
                        <div className="clearfix">
                            <div className="pull-left spacer-bottom">
                                <div className="inline-items">
                                    <FormTextInput inputRef={inputSearchRef} placeholder={placeholder} onChange={onSearchChange} value={searchTerm} large disabled={saving || disabled} loading={loading} icon="search" id="user-search-field" />
                                </div>
                            </div>
                        </div>

                        <ShowHide
                            evaluator={itemsValue.length > 0 || loadingPage}
                            show={(
                                <FormCheckboxList
                                    id={id}
                                    items={loadingPage && itemsValue.length === 0 ? dummyItems : itemsValue}
                                    onChange={onItemChange}
                                    loading={loadingPage || saving}
                                    striped={true}
                                    fullWidth={fill}
                                ></FormCheckboxList>
                            )}
                            hide={(<NonIdealState
                                title={''}
                                description={noDataMessage}
                            />)}
                        />


                        <ShowHide
                            evaluator={totalPages > 1}
                            show={(
                                <ListingPagination currentPage={pageNumber} totalPages={totalPages} loadingData={false} onPageChange={onPageChangeValue} />
                            )}
                        />
                        <FormHelperText danger={true}>{dangerHelperText}</FormHelperText>
                    </Fragment>
                )}
            />
        </div>
    );
}

FormPagedCheckboxList.propTypes = {
    onPageChange: PropTypes.func.isRequired,
    items: PropTypes.array.isRequired,
    selectedItems: PropTypes.array.isRequired,
    onItemSelect: PropTypes.func.isRequired,
    totalItemCount: PropTypes.number.isRequired,
    headingText: PropTypes.string,
    placeholder: PropTypes.string,
    id: PropTypes.string,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    loadingPage: PropTypes.bool,
    saving: PropTypes.bool,
    noDataMessage: PropTypes.string,
    dangerHelperText: PropTypes.string,
    fill: PropTypes.bool
};